/* iter-companion-ping — Green/red/amber pill for the Companion Reader
 * base-URL reachability. Polls the admin ping endpoint every 90 s so
 * a broken deploy shows up in the hub without hunting.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { adminApi, adminAuth } from '@/lib/api';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowLeftRight, Key, RefreshCw, Copy as CopyIcon, Bell, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TONE = {
  green: { icon: CheckCircle2,    cls: 'border-emerald-500/60 bg-emerald-50 text-emerald-800' },
  amber: { icon: AlertTriangle,   cls: 'border-amber-500/60 bg-amber-50 text-amber-900'     },
  red:   { icon: XCircle,         cls: 'border-rose-500/60 bg-rose-50 text-rose-800'         },
};

export default function CompanionPingPill() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const pull = () => {
    setLoading(true);
    axios.get(`${API}/admin/companion-integration/ping`, {
      headers: adminAuth.token ? { Authorization: `Bearer ${adminAuth.token}` } : undefined,
      withCredentials: true,
      timeout: 10000,
    })
      .then((r) => setState(r.data))
      .catch((e) => setState({ status: 'red', reason: e?.response?.data?.detail || 'Ping failed.' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    pull();
    const id = setInterval(pull, 90_000);
    return () => clearInterval(id);
  }, []);

  if (!state && loading) {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-panel-header/50 px-2 py-1 text-[10.5px] text-muted-foreground"
        data-testid="companion-ping-pill-loading"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Companion…
      </div>
    );
  }
  const tone = TONE[state?.status] || TONE.red;
  const Icon = tone.icon;
  const rev = state?.reverse;
  const revLabel = rev
    ? (rev.ok
        ? `reverse ✓ ${rev.stale_min != null ? Math.round(rev.stale_min) + 'm ago' : 'ok'}`
        : `reverse ✗ ${rev.stale_min != null ? Math.round(rev.stale_min) + 'm stale' : 'never'}`)
    : 'reverse: never';
  const title = state?.reason
    ? `Companion · ${state.reason} · ${revLabel}`
    : `Companion · ${state?.http || '—'} in ${state?.latency_ms ?? '—'} ms · ${state?.base || 'unset'} · ${revLabel}`;

  return (
    <>
      <button
      type="button"
      onClick={pull}
      title={title}
      aria-label={title}
      data-testid="companion-ping-pill"
      data-status={state?.status || 'red'}
      data-reverse-ok={rev?.ok ? '1' : '0'}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10.5px] font-medium transition hover:brightness-95 ${tone.cls}`}
    >
      <Icon className="h-3 w-3" />
      <span>Companion</span>
      {state?.latency_ms != null && state?.status !== 'red' && (
        <span className="font-mono-stat opacity-70">{Math.round(state.latency_ms)}ms</span>
      )}
      {state?.status === 'red' && <span className="uppercase tracking-wider">down</span>}
      {/* iter-bidirectional-ping — reverse leg indicator */}
      <span
        className={`ml-1 inline-flex items-center gap-0.5 rounded-sm px-1 text-[9.5px] uppercase tracking-wider ${rev?.ok ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}
        data-testid="companion-ping-pill-reverse"
      >
        <ArrowLeftRight className="h-2.5 w-2.5" />
        {rev?.ok ? '↔' : '½'}
      </span>
      </button>
      {/* iter-companion-secret — separate button so the pill click
          still just refreshes the probe. */}
      <SecretButton />
    </>
  );
}

function SecretButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [secret, setSecret] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [rotatingAll, setRotatingAll] = useState(false);
  const [rotatedKeys, setRotatedKeys] = useState(null);
  const [pingUrlInput, setPingUrlInput] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [heartbeats, setHeartbeats] = useState(null);
  const [testFiring, setTestFiring] = useState(false);
  const [rotations, setRotations] = useState(null);
  const [rotFilter, setRotFilter] = useState({ actor: '', from: '', to: '' });
  const [rotLoading, setRotLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.companionSecretGet(),
      adminApi.companionHeartbeats(24).catch(() => null),
      adminApi.companionRotationHistory({ limit: 25 }).catch(() => ({ rotations: [] })),
    ])
      .then(([s, hb, hist]) => { setSecret(s); setHeartbeats(hb); setRotations(hist?.rotations || []); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load secret'))
      .finally(() => setLoading(false));
  };
  const reloadRotations = () => {
    setRotLoading(true);
    const params = { limit: 100 };
    if (rotFilter.actor)  params.actor = rotFilter.actor;
    if (rotFilter.from)   params.from  = rotFilter.from;
    if (rotFilter.to)     params.to    = rotFilter.to;
    adminApi.companionRotationHistory(params)
      .then((r) => setRotations(r.rotations || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Filter reload failed'))
      .finally(() => setRotLoading(false));
  };
  const rotate = () => {
    if (!window.confirm('Rotate the reverse-ping secret? The old value stops working immediately — update the Companion Reader\'s env right after copying the new one.')) return;
    setRotating(true);
    adminApi.companionSecretRotate()
      .then((r) => { setSecret({ configured: true, value: r.value, source: r.source, rotated_at: r.rotated_at, ping_url: r.ping_url, ping_url_source: r.ping_url_source }); toast.success('Rotated — copy the new value now.'); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Rotate failed'))
      .finally(() => setRotating(false));
  };
  const rotateAll = () => {
    if (!window.confirm('Rotate BOTH the reverse-ping secret AND the CROWNROSE_SERVICE_KEY? Both old values stop working immediately — the Reader must be re-provisioned with both new values RIGHT AFTER copying them here.')) return;
    setRotatingAll(true);
    adminApi.companionKeysRotateAll()
      .then((r) => {
        setRotatedKeys(r);
        setSecret((s) => ({ ...s, configured: true,
                               value: r.ping_secret.value,
                               source: r.ping_secret.source,
                               rotated_at: r.rotated_at,
                               ping_url: r.ping_url,
                               ping_url_source: r.ping_url_source }));
        toast.success('Both keys rotated — copy each value into the Reader env now.');
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Rotate-all failed'))
      .finally(() => setRotatingAll(false));
  };
  const copyValue = async (val, label) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied.`);
    } catch { toast.error('Copy failed — select the value manually.'); }
  };
  const slackTestFire = () => {
    setTestFiring(true);
    adminApi.companionSlackTestFire()
      .then((r) => {
        if (r.delivered) {
          toast.success(`Slack canary delivered to ${r.webhook}. Check the ops channel.`);
        } else if (r.reason === 'no_webhook') {
          toast.error('No Slack webhook configured. Set SLACK_WEBHOOK_URL or the app_config override first.');
        } else {
          toast.error(`Slack post failed: ${r.reason || 'unknown'}`);
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Test-fire failed'))
      .finally(() => setTestFiring(false));
  };
  const copy = async () => {
    if (!secret?.value) return;
    try {
      await navigator.clipboard.writeText(secret.value);
      toast.success('Secret copied. Paste into the Companion Reader\'s env now.');
    } catch { toast.error('Copy failed — select the value manually.'); }
  };
  const copyPingUrl = async () => {
    if (!secret?.ping_url) return;
    try {
      await navigator.clipboard.writeText(secret.ping_url);
      toast.success('Ping URL copied. Paste into the Companion Reader\'s env as COMPANION_PING_URL.');
    } catch { toast.error('Copy failed — select the value manually.'); }
  };
  const saveProdUrl = () => {
    const url = pingUrlInput.trim();
    if (!url) return;
    setSavingUrl(true);
    adminApi.companionPingUrlSet(url)
      .then((r) => {
        setSecret((s) => ({ ...s, ping_url: r.ping_url, ping_url_source: r.source, ping_url_rotated_at: r.rotated_at }));
        setPingUrlInput('');
        toast.success('Prod URL override saved.');
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Save failed'))
      .finally(() => setSavingUrl(false));
  };
  const clearProdUrl = () => {
    setSavingUrl(true);
    adminApi.companionPingUrlClear()
      .then((r) => {
        setSecret((s) => ({ ...s, ping_url: r.ping_url, ping_url_source: r.source, ping_url_rotated_at: null }));
        toast.success('Prod URL override cleared — using env fallback.');
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Clear failed'))
      .finally(() => setSavingUrl(false));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); load(); }}
        className="inline-flex items-center gap-1 rounded-full border border-border bg-panel-header/70 px-2 py-1 text-[10.5px] font-medium text-muted-foreground transition hover:bg-panel-header"
        data-testid="companion-secret-open"
        title="Copy or rotate the Companion reverse-ping shared secret"
      >
        <Key className="h-3 w-3" /> Secret
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="companion-secret-dialog">
          <DialogHeader>
            <DialogTitle>Companion reverse-ping secret</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-[12px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : secret ? (
            <div className="space-y-3">
              <div className="text-[11px] text-muted-foreground">
                Source: <code className="rounded-sm bg-muted px-1">{secret.source}</code>
                {secret.rotated_at && <>· Rotated {new Date(secret.rotated_at).toLocaleString()}</>}
              </div>
              {/* iter-companion-ping-url — the URL the Companion Reader posts heartbeats to. */}
              <div className="rounded-md border border-sky-500/60 bg-sky-50 p-3" data-testid="companion-ping-url-block">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10.5px] uppercase tracking-wider text-sky-800">
                    Heartbeat URL (COMPANION_PING_URL)
                    {secret.ping_url_source && (
                      <span className="ml-1 rounded-sm bg-white/70 px-1 text-[9.5px] normal-case tracking-normal text-sky-700">
                        {secret.ping_url_source}
                      </span>
                    )}
                  </div>
                </div>
                {secret.ping_url ? (
                  <div className="mt-1 flex items-center gap-2">
                    <code
                      className="flex-1 truncate rounded-sm bg-white px-2 py-1.5 font-mono-stat text-[11.5px] text-sky-900"
                      data-testid="companion-ping-url-value"
                    >
                      {secret.ping_url}
                    </code>
                    <Button size="sm" variant="outline" onClick={copyPingUrl} className="gap-1" data-testid="companion-ping-url-copy">
                      <CopyIcon className="h-3 w-3" /> Copy
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1 text-[11px] text-sky-900">
                    Set <code>COMPANION_PING_URL</code> (or <code>FRONTEND_URL</code>) in the parent env to enable.
                  </div>
                )}
                <div className="mt-1.5 text-[10.5px] text-sky-800/80">
                  Reader must <code>POST</code> this URL every 5 min with header
                  <code className="ml-1">X-Companion-Secret</code>.
                </div>
                {/* iter-prod-url-swap — inline editable prod override so admins can
                    flip preview→prod without touching .env. */}
                <div className="mt-2 border-t border-sky-500/30 pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-sky-800">
                    Override with a custom prod URL
                    {secret.ping_url_source === 'db-override' && secret.ping_url_rotated_at && (
                      <span className="ml-1 rounded-sm bg-white/70 px-1 text-[9.5px] normal-case tracking-normal text-sky-700">
                        active · {new Date(secret.ping_url_rotated_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <input
                      type="text"
                      value={pingUrlInput}
                      onChange={(e) => setPingUrlInput(e.target.value)}
                      placeholder="https://crownnrose.com/api/companion-integration/reverse-ping"
                      className="flex-1 rounded-sm border border-sky-500/40 bg-white px-2 py-1 font-mono-stat text-[10.5px] text-sky-900 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      data-testid="companion-ping-url-input"
                    />
                    <Button size="sm" variant="outline" onClick={saveProdUrl}
                            disabled={savingUrl || !pingUrlInput.trim()}
                            className="h-7 gap-1 text-[10.5px]"
                            data-testid="companion-ping-url-save">
                      Save
                    </Button>
                    {secret.ping_url_source === 'db-override' && (
                      <Button size="sm" variant="ghost" onClick={clearProdUrl}
                              disabled={savingUrl}
                              className="h-7 gap-1 text-[10.5px] text-sky-800"
                              data-testid="companion-ping-url-clear">
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {/* iter-companion-heartbeat-history — 24 h sparkline. */}
              <HeartbeatSparkline data={heartbeats} />
              {secret.configured ? (
                <div className="rounded-md border border-amber-500/60 bg-amber-50 p-3">
                  <div className="text-[10.5px] uppercase tracking-wider text-amber-800">Current secret</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code
                      className="flex-1 truncate rounded-sm bg-white px-2 py-1.5 font-mono-stat text-[12px] text-amber-900"
                      data-testid="companion-secret-value"
                    >
                      {secret.value}
                    </code>
                    <Button size="sm" variant="outline" onClick={copy} className="gap-1" data-testid="companion-secret-copy">
                      <CopyIcon className="h-3 w-3" /> Copy
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-rose-500/60 bg-rose-50 p-3 text-[12px] text-rose-800">
                  No secret is configured. The reverse-ping endpoint is currently open. Rotate below to lock it down.
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-[10.5px] text-muted-foreground">
                  Rotating persists a DB override — env value stays untouched so a redeploy can&apos;t re-introduce the old key.
                </p>
                <Button size="sm" variant="destructive" onClick={rotate} disabled={rotating} className="gap-1.5" data-testid="companion-secret-rotate">
                  {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {rotating ? 'Rotating…' : 'Rotate now'}
                </Button>
              </div>
              {/* Feb-2026 · Rotation audit log — append-only paper
                  trail for every key rotation and URL swap. */}
              <RotationHistoryPanel rows={rotations}
                                        filter={rotFilter}
                                        onFilter={setRotFilter}
                                        onReload={reloadRotations}
                                        loading={rotLoading} />
              {/* Feb-2026 · Slack canary — proves the contract-diff
                  alert channel is wired end-to-end. */}
              <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-500/60 bg-emerald-50 p-3"
                   data-testid="companion-slack-test-fire-row">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-emerald-900">Slack alert canary</div>
                  <p className="mt-0.5 text-[10.5px] text-emerald-900/80">Sends a test message so ops can confirm the contract-diff channel is wired end-to-end.</p>
                </div>
                <Button size="sm" variant="outline" onClick={slackTestFire}
                        disabled={testFiring}
                        className="gap-1.5"
                        data-testid="companion-slack-test-fire">
                  {testFiring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
                  Test-fire Slack
                </Button>
              </div>
              {/* Feb-2026 · Combined rotation — both COMPANION_SECRET
                  and CROWNROSE_SERVICE_KEY in one click. */}
              <div className="rounded-md border border-fuchsia-500/60 bg-fuchsia-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-fuchsia-900">
                      One-click key rotation
                    </div>
                    <p className="mt-0.5 text-[10.5px] text-fuchsia-900/80">
                      Rotates BOTH the reverse-ping secret AND the shared webhook signing key.
                      Reader must be re-provisioned with both new values immediately.
                    </p>
                  </div>
                  <Button size="sm" variant="destructive" onClick={rotateAll}
                          disabled={rotatingAll}
                          className="gap-1.5"
                          data-testid="companion-keys-rotate-all">
                    {rotatingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                   : <RefreshCw className="h-3.5 w-3.5" />}
                    {rotatingAll ? 'Rotating…' : 'Rotate both keys'}
                  </Button>
                </div>
                {rotatedKeys && (
                  <div className="mt-3 space-y-2" data-testid="companion-keys-rotated-block">
                    <RotatedKeyRow label="COMPANION_SECRET (reverse-ping)"
                                       value={rotatedKeys.ping_secret?.value}
                                       onCopy={() => copyValue(rotatedKeys.ping_secret?.value,
                                                                    'COMPANION_SECRET')}
                                       testid="companion-keys-ping-copy" />
                    <RotatedKeyRow label="CROWNROSE_SERVICE_KEY (webhook + admin)"
                                       value={rotatedKeys.service_key?.value}
                                       onCopy={() => copyValue(rotatedKeys.service_key?.value,
                                                                    'CROWNROSE_SERVICE_KEY')}
                                       testid="companion-keys-service-copy" />
                    <div className="text-[9.5px] text-fuchsia-900/80">
                      Rotated {new Date(rotatedKeys.rotated_at).toLocaleString()}. These
                      values will not be shown again — copy them now.
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-[12px] text-muted-foreground">Could not load.</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


// Feb-2026 — one-click rotate-all display row.
function RotationHistoryPanel({ rows, filter, onFilter, onReload, loading }) {
  if (!rows) return null;
  const set = (k, v) => onFilter?.({ ...filter, [k]: v });
  const label = (action) => ({
    rotate_reverse_ping_secret: 'Rotated COMPANION_SECRET',
    rotate_all_keys:            'Rotated BOTH keys',
    set_ping_url_override:      'Ping URL override set',
    clear_ping_url_override:    'Ping URL override cleared',
  }[action] || action);
  const downloadCsv = () => {
    // Anchor click so we inherit the admin session cookie. Include
    // the current filters so the download matches the on-screen slice.
    const params = { limit: 5000 };
    if (filter?.actor) params.actor = filter.actor;
    if (filter?.from)  params.from  = filter.from;
    if (filter?.to)    params.to    = filter.to;
    const a = document.createElement('a');
    a.href = adminApi.companionRotationHistoryCsvUrl(params);
    a.download = 'companion-key-rotations.csv';
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('CSV downloading — matches your current filter slice.');
  };
  const filtersActive = !!(filter?.actor || filter?.from || filter?.to);
  return (
    <div className="rounded-md border border-border bg-background p-3"
         data-testid="companion-rotation-history">
      <div className="mb-2 flex items-center justify-between text-[10.5px]">
        <span className="uppercase tracking-wider text-muted-foreground">Recent rotations</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={downloadCsv}
                  data-testid="companion-rotation-history-csv"
                  title={filtersActive ? 'Download filtered slice as CSV' : 'Download all as CSV'}
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 text-[10px] hover:bg-secondary">
            <Download className="h-2.5 w-2.5" />
            CSV{filtersActive ? ' (filtered)' : ''}
          </button>
          <span className="text-muted-foreground">{rows.length}</span>
        </div>
      </div>
      {/* Feb-2026 · Per-actor & date-range filters. */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5 rounded-sm border border-border bg-muted/30 p-1.5"
           data-testid="companion-rotation-filters">
        <input type="text" placeholder="Actor…" value={filter?.actor || ''}
               onChange={(e) => set('actor', e.target.value)}
               data-testid="companion-rotation-filter-actor"
               className="h-6 w-24 rounded-sm border border-border bg-background px-1.5 text-[10.5px] outline-none focus:ring-1 focus:ring-primary" />
        <input type="date" value={filter?.from || ''}
               onChange={(e) => set('from', e.target.value)}
               data-testid="companion-rotation-filter-from"
               className="h-6 rounded-sm border border-border bg-background px-1 text-[10.5px]" />
        <span className="text-[10px] text-muted-foreground">→</span>
        <input type="date" value={filter?.to || ''}
               onChange={(e) => set('to', e.target.value)}
               data-testid="companion-rotation-filter-to"
               className="h-6 rounded-sm border border-border bg-background px-1 text-[10.5px]" />
        <button type="button" onClick={onReload}
                disabled={loading}
                data-testid="companion-rotation-filter-apply"
                className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {loading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : null}
          Apply
        </button>
        {filtersActive && (
          <button type="button"
                  onClick={() => { onFilter?.({ actor: '', from: '', to: '' }); setTimeout(onReload, 0); }}
                  data-testid="companion-rotation-filter-clear"
                  className="text-[10px] text-muted-foreground underline hover:text-foreground">
            Clear
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-sm border border-dashed border-border bg-muted/30 p-3 text-center text-[10.5px] text-muted-foreground"
             data-testid="companion-rotation-history-empty">
          {filtersActive ? 'No rotations match your filters.' : 'No key rotations recorded yet.'}
        </div>
      ) : (
        <ul className="space-y-1 text-[11px]">
          {rows.slice(0, 6).map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2 border-b border-border/60 pb-1 last:border-0 last:pb-0"
                data-testid={`companion-rotation-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{label(r.action)}</div>
                <div className="text-[9.5px] text-muted-foreground">
                  {(r.keys || []).join(', ')}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-stat text-[10px]">{new Date(r.at).toLocaleString()}</div>
                <div className="text-[9.5px] text-muted-foreground">{r.actor}</div>
              </div>
            </li>
          ))}
          {rows.length > 6 && (
            <li className="pt-0.5 text-center text-[9.5px] text-muted-foreground">
              …{rows.length - 6} more (download CSV to see all)
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function RotatedKeyRow({ label, value, onCopy, testid }) {
  return (
    <div className="rounded-sm border border-fuchsia-500/40 bg-white p-2">
      <div className="text-[9.5px] uppercase tracking-wider text-fuchsia-800">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded-sm bg-fuchsia-50 px-2 py-1 font-mono-stat text-[11px] text-fuchsia-900"
              data-testid={`${testid}-value`}>
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={onCopy} className="gap-1 h-7"
                data-testid={testid}>
          <CopyIcon className="h-3 w-3" /> Copy
        </Button>
      </div>
    </div>
  );
}

// iter-companion-heartbeat-history — mini SVG sparkline for the last
// 24 h of Companion Reader heartbeats. Falls back to a friendly empty
// state when the Reader hasn't posted yet.
function HeartbeatSparkline({ data }) {
  if (!data || data.total === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-[10.5px] text-muted-foreground"
           data-testid="companion-heartbeat-sparkline-empty">
        No heartbeats received in the last 24 h. Once the Reader starts posting to the URL above, a sparkline will render here.
      </div>
    );
  }
  const series = data.series || [];
  const width = 260;
  const height = 36;
  const step = series.length > 1 ? width / (series.length - 1) : 0;
  const points = series.map((r, i) => {
    const x = Math.round(i * step);
    const y = r.healthy ? 4 : height - 4;
    return `${x},${y}`;
  }).join(' ');
  const uptime = data.uptime_pct;
  const uptimeCls = uptime >= 99 ? 'text-emerald-700 dark:text-emerald-400'
                  : uptime >= 90 ? 'text-amber-700 dark:text-amber-400'
                  : 'text-rose-700 dark:text-rose-400';
  return (
    <div className="rounded-md border border-border bg-background p-3"
         data-testid="companion-heartbeat-sparkline">
      <div className="flex items-center justify-between text-[10.5px]">
        <div className="uppercase tracking-wider text-muted-foreground">Last 24 h heartbeats</div>
        <div className="flex items-center gap-2">
          <span className={`font-mono-stat font-semibold ${uptimeCls}`}
                data-testid="companion-heartbeat-uptime">
            {uptime != null ? `${uptime}%` : '—'}
          </span>
          <span className="text-muted-foreground">
            {data.healthy}/{data.total}
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="mt-1 h-9 w-full"
           preserveAspectRatio="none" aria-label="Heartbeat sparkline">
        <line x1="0" y1={height / 2} x2={width} y2={height / 2}
              stroke="currentColor" strokeOpacity="0.15" strokeWidth="0.5" />
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={uptime >= 99 ? 'text-emerald-500' : uptime >= 90 ? 'text-amber-500' : 'text-rose-500'}
        />
        {series.map((r, i) => (
          <circle
            key={i}
            cx={Math.round(i * step)}
            cy={r.healthy ? 4 : height - 4}
            r="1.5"
            className={r.healthy ? 'fill-emerald-500' : 'fill-rose-500'}
          />
        ))}
      </svg>
      <div className="mt-0.5 flex justify-between text-[9.5px] text-muted-foreground">
        <span>-24 h</span>
        <span>now</span>
      </div>
    </div>
  );
}
